import { useEffect, useState } from 'react';
import { Address } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { usePostCheckJoinDuplicateApi, usePostSendEmailVerificationApi, usePostVerifyCodeApi } from '@/api/auth';
import Field from '@/components/common/Field/Field';
import InputAddress from '@/components/common/Input/InputAddress/InputAddress';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { emailOptions, monthOptions, yearOptions } from '@/constants/option';
import { useModalStore } from '@/store/modalStore';
import { formatCell } from '@/utils/formatting';

import * as S from './style';
import { useSignUpStore } from '../SignUpContext/SignUpContext';

interface RegisterField {
	name: string;
	enName: string;
	birth: { year: string; month: string; day: string };
	id: string;
	idCheck: boolean;
	password: string;
	passwordCheck: string;
	gender: string;
	address: { zipcode: string; address: string; detailAddress: string };
	cell: string;
	email: { front: string; back: string; code: string; verificationCode: string };
}
const BasicInfoForm = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isSendEmail, setIsSendEmail] = useState(false);
	const { updateBasicInfo, setStep, basicInfo, setCurrentStep } = useSignUpStore();
	const { showModal } = useModalStore();
	const userRegisterSchema = z
		.object({
			name: z.string().min(1, { message: '이름을 입력해 주세요.' }),
			enName: z.string().min(1, { message: '영문이름을 입력해 주세요.' }),
			birth: z.object({
				year: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
				month: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
				day: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
			}),
			id: z
				.string()
				.min(1, { message: '6~20자의 영문, 숫자만 입력 가능합니다.' })
				.min(6, { message: '6~20자의 영문, 숫자만 입력 가능합니다.' })
				.max(20, { message: '6~20자의 영문, 숫자만 입력 가능합니다.' })
				.regex(/^[A-Za-z0-9]+$/, { message: '6~20자의 영문, 숫자만 입력 가능합니다.' }),
			idCheck: z.union([z.literal(true), z.literal(false)]).refine((val) => val === true, {
				message: '아이디 중복 확인 버튼을 눌러주세요.',
			}),
			password: z
				.string()
				.min(1, { message: '6~20자의 영문, 숫자,특수문자만 입력 가능합니다.' })
				.min(6, { message: '6~20자의 영문, 숫자,특수문자만 입력 가능합니다.' })
				.max(20, { message: '6~20자의 영문, 숫자,특수문자만 입력 가능합니다.' })
				.regex(/^[A-Za-z0-9!@#$%^&*()-_+=]+$/, { message: '6~20자의 영문, 숫자,특수문자만 입력 가능합니다.' }),
			passwordCheck: z.string().min(1, { message: '비밀번호가 일치하지 않습니다.' }),
			email: z.object({
				front: z.string().min(1, { message: '이메일 주소를 입력해 주세요.' }),
				back: z.string().min(1, { message: '이메일 주소를 입력해 주세요.' }),
				code: z.string(),
				verificationCode: z.string(),
			}),
			gender: z.string().min(1, { message: '성별을 선택해 주세요.' }),
			cell: z.string().min(1, { message: '휴대폰 번호를 입력해 주세요.' }),
			address: z.object({
				zipcode: z.string().min(1, { message: '우편번호를 입력해 주세요.' }),
				address: z.string().min(1, { message: '주소를 입력해 주세요.' }),
				detailAddress: z.string().min(1, { message: '상세주소를 입력해 주세요.' }),
			}),
		})
		.refine((data) => data.password === data.passwordCheck, {
			message: '비밀번호가 일치하지 않습니다.',
			path: ['passwordCheck'],
		});

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		setError,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			enName: '',
			idCheck: false,
			birth: { year: '', month: '', day: '' },
			id: '',
			cell: '',
			password: '',
			passwordCheck: '',
			email: { front: '', back: '', code: '직접입력', verificationCode: '' },
			gender: '남',
			address: { zipcode: '', address: '', detailAddress: '' },
		},
	});

	const { onChange, ...rest } = register('email.verificationCode');

	const { mutateAsync: VerifyCode } = usePostVerifyCodeApi();
	const { mutateAsync: SendEmailVerification, isPending } = usePostSendEmailVerificationApi();
	const { mutateAsync: CheckJoinDuplicate } = usePostCheckJoinDuplicateApi();
	const getLastDayOfMonth = (year: string, month: string) => {
		const date = new Date(Number(year), Number(month), 0);
		return date.getDate();
	};
	const days = Array.from({ length: getLastDayOfMonth(watch('birth.year'), watch('birth.month')) }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
	const handleBirthYear = (label: string) => {
		setValue('birth.year', label, { shouldValidate: true });
	};
	const handleBirthMonth = (label: string) => {
		setValue('birth.month', label, { shouldValidate: true });
	};
	const handleBirthDay = (label: string) => {
		setValue('birth.day', label, { shouldValidate: true });
	};
	const handleAddress = (val: Address) => {
		setValue(
			'address',
			{
				zipcode: val.zonecode,
				address: val.address,
				detailAddress: '',
			},
			{ shouldValidate: true }
		);
	};

	const onSubmit = (data: RegisterField) => {
		updateBasicInfo(data);
		console.log('data', data);
		setStep('직장정보 입력');
		setCurrentStep('직장정보 입력');
	};

	const handleSendEmailVerification = async () => {
		const res = await SendEmailVerification({ email: `${watch('email.front')}@${watch('email.back')}` });
		if (res.data.success) {
			showModal('입력하신 이메일로 인증번호가 발송되었습니다.\n이메일을 확인하신 후 인증번호를 입력해 주세요.');
		}
		setIsSendEmail(true);
	};
	const handleVerifyCode = async () => {
		const email = `${watch('email.front')}@${watch('email.back')}`;
		const code = watch('email.verificationCode');
		const res = await VerifyCode({ email, code });
		if (res.data.success) {
			showModal('인증이 완료되었습니다.');
			setIsEmailVerified(true);
		} else {
			showModal('인증번호가 일치하지 않습니다.');
			setIsEmailVerified(false);
		}
	};
	const handleCheckId = async () => {
		const res = await CheckJoinDuplicate({
			param: watch('id'),
			param_type: 'id',
		});
		if (res.data.success) {
			showModal('사용할 수 있는 아이디입니다.');
			setValue('idCheck', true, { shouldValidate: true }); // 확인 완료로 세팅
		} else {
			showModal('사용할 수 없는 아이디입니다.');
			setValue('idCheck', false, { shouldValidate: true });
			setError('id', {
				type: 'manual',
				message: '사용할 수 없는 아이디입니다.',
			});
		}
	};
	useEffect(() => {
		const hasInfo = basicInfo.name !== '' || basicInfo.enName !== '' || basicInfo.id !== '';

		if (hasInfo) {
			console.log('basicInfo', basicInfo);
			setValue('name', basicInfo.name);
			setValue('enName', basicInfo.enName);
			setValue('birth.year', basicInfo.birth.year);
			setValue('birth.month', basicInfo.birth.month);
			setValue('birth.day', basicInfo.birth.day);
			setValue('id', basicInfo.id);
			setValue('idCheck', !!basicInfo.id);
			setValue('cell', basicInfo.cell);
			setValue('password', basicInfo.password);
			setValue('passwordCheck', basicInfo.passwordCheck);
			setValue('address.address', basicInfo.address.address);
			setValue('address.zipcode', basicInfo.address.zipcode);
			setValue('address.detailAddress', basicInfo.address.detailAddress);
			setValue('gender', basicInfo.gender);
			setValue('email.front', basicInfo.email.front);
			setValue('email.back', basicInfo.email.back);
			setValue('email.code', basicInfo.email.code);
		}
	}, [basicInfo]);
	return (
		<S.FormContainer>
			<S.FieldBox>
				<S.Danger>*필수 입력 값</S.Danger>
				<Field title="*이름" error={errors.name?.message ?? ''}>
					<InputText placeholder="예) 홍길동" {...register('name')} isError={!!errors.name?.message} />
				</Field>
				<Field title="*영문이름" error={errors.enName?.message ?? ''}>
					<InputText placeholder="예) Hong Gildong" {...register('enName')} isError={!!errors.enName?.message} />
				</Field>
				<Field title="*생년월일" error={errors.birth?.year?.message ?? errors.birth?.day?.message ?? errors.birth?.month?.message ?? ''}>
					<S.FlexRow>
						<InputSelect options={yearOptions} selectedOption={watch('birth.year')} setSelectedOption={handleBirthYear} defaultValue="년도" />
						<InputSelect options={monthOptions} selectedOption={watch('birth.month')} setSelectedOption={handleBirthMonth} defaultValue="월" />
						<InputSelect options={days} selectedOption={watch('birth.day')} setSelectedOption={handleBirthDay} defaultValue="일" />
					</S.FlexRow>
				</Field>
				<Field title="*성별" error={errors.gender?.message ?? ''} gap="0.8rem">
					<S.GenderBox>
						<S.GenderRadio>
							<label>
								<input type="radio" value="남" {...register('gender')} />남
							</label>
						</S.GenderRadio>
						<S.GenderRadio>
							<label>
								<input type="radio" value="여" {...register('gender')} />여
							</label>
						</S.GenderRadio>
					</S.GenderBox>
				</Field>
				<Field title="*아이디" error={errors.id?.message ?? errors.idCheck?.message ?? ''}>
					<S.AddressBox>
						<InputText
							{...register('id')}
							input={(e) => {
								register('id').onChange(e);
								setValue('idCheck', false, { shouldValidate: true });
							}}
							placeholder="6~20자의 영문, 숫자만 가능"
							isError={!!errors.id?.message}
						/>
						<S.FindButton type="button" onClick={handleCheckId} disabled={!!errors.id?.message || watch('id') === ''}>
							확인
						</S.FindButton>
					</S.AddressBox>
				</Field>
				<Field title="*비밀번호" error={errors.password?.message ?? ''}>
					<InputText type="password" {...register('password')} placeholder="6~20자의 영문, 숫자,특수문자" isError={!!errors.password?.message} />
				</Field>
				<Field title="*비밀번호 확인" error={errors.passwordCheck?.message ?? ''}>
					<InputText type="password" {...register('passwordCheck')} placeholder="비밀번호 재입력" isError={!!errors.passwordCheck?.message} />
				</Field>
				<Field title="*주소" error={errors.address?.address?.message ?? errors.address?.detailAddress?.message ?? ''}>
					<S.AddressContentBox>
						<S.AddressBox>
							<InputText readonly {...register('address.zipcode')} />
							<S.FindButton type="button" onClick={() => setIsOpen(true)}>
								우편번호 찾기
							</S.FindButton>
						</S.AddressBox>
						<S.AddressBox>
							<InputText readonly {...register('address.address')} />
						</S.AddressBox>
						<S.AddressBox>
							<InputText
								{...register('address.detailAddress')}
								placeholder="상세주소 입력"
								isError={!!errors.address?.address?.message || !!errors.address?.detailAddress?.message}
							/>
						</S.AddressBox>
					</S.AddressContentBox>
				</Field>
				<Field title="*휴대폰 번호" error={errors.cell?.message ?? ''}>
					<InputText
						{...Object.assign({}, register('cell'), {
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
								const value = formatCell(e);
								console.log(value);
								setValue('cell', value, { shouldValidate: true });
							},
						})}
						placeholder="‘-’없이 숫자만 입력"
						isError={!!errors.cell?.message}
					/>
				</Field>
				<Field title="*이메일 인증" gap="0.8rem">
					<Field error={errors.email?.back?.message ?? errors.email?.front?.message ?? errors.email?.code?.message ?? ''}>
						<S.FlexRow>
							<InputText
								{...Object.assign({}, register('email.front'), {
									onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
										setIsSendEmail(false);
									},
								})}
								isError={!!errors.email?.front?.message}
							/>
							@
							<InputText
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.back', e.target.value, { shouldValidate: true });
									setIsSendEmail(false);
								}}
								value={watch('email.code') !== '' && watch('email.code') !== '직접입력' ? watch('email.code') : watch('email.back')}
								readonly={watch('email.code') !== '' && watch('email.code') !== '직접입력'}
							/>
							<InputSelect
								options={emailOptions}
								selectedOption={watch('email.code')}
								setSelectedOption={(val) => {
									setValue('email.code', val, { shouldValidate: true });
									setIsSendEmail(false);
									if (val !== '직접입력') setValue('email.back', val, { shouldValidate: true });
								}}
								defaultValue="직접입력"
							/>
							<S.FindButton
								type="button"
								onClick={handleSendEmailVerification}
								disabled={watch('email.front') === '' || watch('email.back') === '' || watch('email.code') === '' || isPending}
							>
								{!isSendEmail ? '전송' : '재전송'}
							</S.FindButton>
						</S.FlexRow>
					</Field>
					<S.FlexRow>
						<InputText
							{...rest}
							input={(e) => {
								onChange(e); // register에서 제공한 onChange 호출
								setIsEmailVerified(false);
								console.log(e.target.value, e.target.value === '', isEmailVerified);
							}}
							placeholder="인증번호 입력"
						/>
						<S.FindButton type="button" onClick={handleVerifyCode} disabled={watch('email.verificationCode') === '' || isEmailVerified}>
							인증
						</S.FindButton>
					</S.FlexRow>
					<S.Danger>※ 이메일 인증의 경우 받는 메일서버에 따라 도착이 지연되거나 스팸으로 분류될 수 있습니다.</S.Danger>
				</Field>
			</S.FieldBox>
			<S.NextButton
				type="submit"
				onClick={handleSubmit(onSubmit)}
				disabled={
					!isEmailVerified ||
					watch('name') === '' ||
					watch('enName') === '' ||
					watch('birth.year') === '' ||
					watch('birth.month') === '' ||
					watch('birth.day') === '' ||
					watch('id') === '' ||
					watch('cell') === '' ||
					watch('password') === '' ||
					watch('passwordCheck') === '' ||
					watch('email.front') === '' ||
					watch('email.back') === '' ||
					watch('email.code') === '' ||
					watch('email.verificationCode') === '' ||
					watch('address.zipcode') === '' ||
					watch('address.address') === '' ||
					watch('address.detailAddress') === ''
				}
			>
				다음
			</S.NextButton>
			{isOpen && <InputAddress setIsOpen={setIsOpen} setAddress={handleAddress} />}
		</S.FormContainer>
	);
};

export default BasicInfoForm;
